import { motion } from 'framer-motion';
import { Shield, Zap, HeadphonesIcon, TrendingUp, Lock, Globe, FileCheck, Award, Lightbulb } from 'lucide-react';

const features = [
  {
    icon: Zap,
    title: 'Lightning Fast',
    desc: 'Get your GSTIN, company registration, and Udyam certificate processed within 24–72 hours.',
    color: 'from-amber-500 to-yellow-400',
    glow: 'rgba(245,158,11,0.25)',
  },
  {
    icon: Shield,
    title: 'Bank-Grade Security',
    desc: '256-bit SSL encryption, ISO-certified processes, and strict data privacy compliance.',
    color: 'from-blue-500 to-cyan-400',
    glow: 'rgba(6,182,212,0.25)',
  },
  {
    icon: HeadphonesIcon,
    title: '24/7 Expert Support',
    desc: 'Dedicated CA, CS, and legal professionals available whenever you need help.',
    color: 'from-purple-500 to-violet-400',
    glow: 'rgba(139,92,246,0.25)',
  },
  {
    icon: TrendingUp,
    title: 'AI-Powered Insights',
    desc: 'Smart business recommendations, compliance alerts, and growth opportunities using AI.',
    color: 'from-green-500 to-emerald-400',
    glow: 'rgba(16,185,129,0.25)',
  },
  {
    icon: Lock,
    title: '100% Govt Compliant',
    desc: 'All filings directly with MCA, GSTN, Income Tax, and other official government portals.',
    color: 'from-rose-500 to-red-400',
    glow: 'rgba(239,68,68,0.25)',
  },
  {
    icon: Globe,
    title: 'Pan-India Presence',
    desc: 'Serving MSMEs across 500+ cities with local experts who understand regional requirements.',
    color: 'from-sky-500 to-blue-400',
    glow: 'rgba(14,165,233,0.25)',
  },
  {
    icon: FileCheck,
    title: 'Transparent Pricing',
    desc: 'No hidden fees. What you see is what you pay. Money-back guarantee on all services.',
    color: 'from-teal-500 to-cyan-400',
    glow: 'rgba(20,184,166,0.25)',
  },
  {
    icon: Award,
    title: 'Award-Winning Platform',
    desc: 'Recognized by NASSCOM and Startup India as one of the top MSME service platforms.',
    color: 'from-orange-500 to-amber-400',
    glow: 'rgba(249,115,22,0.25)',
  },
];

export function Features() {
  return (
    <section className="py-24 bg-gradient-to-b from-slate-100 to-slate-50 dark:from-[#0d1e3a] dark:to-[#0A1628] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-72 h-72 bg-blue-600/5 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-600/5 rounded-full blur-[80px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 text-sm font-bold mb-6">
            <Lightbulb className="w-4 h-4" /> Why 10,000+ Choose Us
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Why Indian MSMEs Trust Us
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-2xl mx-auto">
            Built specifically for Indian entrepreneurs with features no other platform offers.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feat, index) => (
            <motion.div
              key={feat.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08, duration: 0.5 }}
              whileHover={{ y: -5 }}
              className="group relative bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] rounded-2xl p-6 hover:border-blue-500/50 dark:hover:border-blue-400/50 transition-all duration-300 shadow-sm hover:shadow-md dark:shadow-xl overflow-hidden"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="text-4xl font-black text-slate-200 dark:text-white/10 group-hover:text-blue-500/20 dark:group-hover:text-blue-400/20 transition-colors">
                  0{index + 1}
                </div>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feat.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <feat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-slate-900 dark:text-white font-bold text-lg mb-2">{feat.title}</h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">{feat.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
